import { createContext, useContext, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const DialogCtx = createContext(null);

export function DialogProvider({ children }) {
  const [state, setState] = useState(null);
  const close = () => setState(null);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    setState({ kind: "confirm", opts, resolve });
  }), []);

  const prompt = useCallback((opts) => new Promise((resolve) => {
    setState({ kind: "prompt", opts, resolve, value: opts.defaultValue || "" });
  }), []);

  const value = { confirm, prompt };

  return (
    <DialogCtx.Provider value={value}>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => { if (!o) { state?.resolve(null); close(); } }}>
        <DialogContent className="bg-[#171717] border-[#262626] text-white max-w-md">
          {state?.kind === "confirm" && (
            <>
              <DialogHeader>
                <DialogTitle data-testid="dialog-title">{state.opts.title || "¿Confirmar?"}</DialogTitle>
                {state.opts.description && (
                  <DialogDescription className="text-neutral-400 pt-2">{state.opts.description}</DialogDescription>
                )}
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button data-testid="dialog-cancel" variant="ghost" onClick={() => { state.resolve(false); close(); }}>
                  {state.opts.cancelText || "Cancelar"}
                </Button>
                <Button
                  data-testid="dialog-confirm"
                  onClick={() => { state.resolve(true); close(); }}
                  className={state.opts.destructive
                    ? "bg-[#EF4444] hover:bg-[#DC2626]"
                    : "bg-emerald-600 hover:bg-emerald-700"}
                >
                  {state.opts.confirmText || "Confirmar"}
                </Button>
              </DialogFooter>
            </>
          )}
          {state?.kind === "prompt" && (
            <PromptForm state={state} setState={setState} close={close} />
          )}
        </DialogContent>
      </Dialog>
    </DialogCtx.Provider>
  );
}

function PromptForm({ state, setState, close }) {
  const [val, setVal] = useState(state.value || "");
  const submit = (e) => {
    e.preventDefault();
    if (state.opts.required && !val.trim()) return;
    state.resolve(val);
    close();
  };
  return (
    <form onSubmit={submit}>
      <DialogHeader>
        <DialogTitle data-testid="dialog-title">{state.opts.title || "Introduce un valor"}</DialogTitle>
        {state.opts.description && (
          <DialogDescription className="text-neutral-400 pt-2">{state.opts.description}</DialogDescription>
        )}
      </DialogHeader>
      <div className="py-4">
        {state.opts.label && <Label className="text-neutral-400 text-xs uppercase tracking-wider mb-2 block">{state.opts.label}</Label>}
        {state.opts.multiline ? (
          <Textarea data-testid="dialog-input" autoFocus rows={4} value={val} onChange={(e) => setVal(e.target.value)} className="bg-[#0A0A0A] border-[#262626]" placeholder={state.opts.placeholder || ""} />
        ) : (
          <Input data-testid="dialog-input" autoFocus value={val} onChange={(e) => setVal(e.target.value)} className="bg-[#0A0A0A] border-[#262626]" placeholder={state.opts.placeholder || ""} />
        )}
      </div>
      <DialogFooter className="gap-2">
        <Button data-testid="dialog-cancel" type="button" variant="ghost" onClick={() => { state.resolve(null); close(); }}>Cancelar</Button>
        <Button data-testid="dialog-submit" type="submit" className="bg-[#EF4444] hover:bg-[#DC2626]">{state.opts.confirmText || "Confirmar"}</Button>
      </DialogFooter>
    </form>
  );
}

export function useDialog() {
  const ctx = useContext(DialogCtx);
  if (!ctx) throw new Error("useDialog outside provider");
  return ctx;
}
